import { useAtomValue, useSetAtom } from "jotai";
import { useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FCC, UserType } from "types";

import { useIsAnonymousUser } from "@/hooks/useIsAnonymousUser";
import { useQueryParam } from "@/hooks/useQueryParam";
import { DataContext } from "@/provider/DataContextProvider";
import { accessTokenAtom, destinationAtom } from "@/utils/atoms";

interface Props {
  user: UserType;
}

export const ProtectedRoute: FCC<Props> = ({ children, user }) => {
  const token = useAtomValue(accessTokenAtom);
  const setDestination = useSetAtom(destinationAtom);
  const [code] = useQueryParam("code");
  const navigate = useNavigate();
  const location = useLocation();
  const [isAnonymousUser] = useIsAnonymousUser();
  const { dataContext } = useContext(DataContext);
  const isEmailVerified = dataContext?.isEmailVerified ?? true;
  const authorized = !!token;

  // This redirects us to /welcome, if the user is not authenticated and the email not verified.
  useEffect(() => {
    const allowedPathsIfNotVerified = [
      "/welcome",
      "/welcome-esim",
      "/onboarding",
    ];

    const isPartnerApp = window.location.hostname.startsWith("partner.");

    if (isAnonymousUser && isPartnerApp) {
      navigate("/welcome", {
        replace: true,
      });
    }

    // Skip email verification logic entirely for partner logins (they cannot be email-verified etc.)
    if (isPartnerApp) {
      return;
    }

    if (
      (!authorized || !isEmailVerified) &&
      !allowedPathsIfNotVerified.some((path) =>
        location.pathname.startsWith(path),
      )
    ) {
      const searchParams = new URLSearchParams();

      if (location.pathname !== "/") {
        setDestination(`${location.pathname}${location.search}`);
      }

      if (code) {
        searchParams.set("code", code);
      }

      navigate({
        pathname: "/welcome",
        search: searchParams.toString(),
      });
    }
  }, [
    authorized,
    code,
    location.pathname,
    location.search,
    user,
    navigate,
    setDestination,
    isAnonymousUser,
    isEmailVerified,
  ]);

  return <>{children}</>;
};
